/** `GET /v1/route-traces?tag_code=|lot_number=` (PRODUCTION_ORDER VIEW) — เส้นทางการผลิตจริงเทียบแผน */

import { callJson, type StockFailure, type StockRequestOptions } from '../inventory/stock';

export interface RouteTraceMachine {
  readonly machine_code: string;
  readonly name: string;
}

export interface RouteTraceConfirmation {
  readonly confirmation_id: string;
  readonly started_at: string | null;
  readonly ended_at: string | null;
  readonly started_by_employee_id: string | null;
  readonly qty_good: string;
  readonly qty_scrap: string;
  readonly qty_rework: string;
  readonly status: string;
  readonly machines: readonly RouteTraceMachine[];
}

/** LOT = ตามการเชื่อม lot จริง; ORDER = ไม่มีการเชื่อม lot ที่ขั้นนั้น จึงแสดงทุกรอบของใบสั่งผลิต */
export type RouteTraceLinkage = 'LOT' | 'ORDER';

export interface RouteTrace {
  readonly order_no: string;
  readonly product_code: string;
  readonly product_name: string;
  readonly planned_route: readonly {
    readonly operation_seq: number;
    readonly work_center_code: string;
    readonly work_center_name: string;
  }[];
  readonly actual: readonly {
    readonly operation_seq: number;
    readonly work_center_code: string;
    readonly linkage: RouteTraceLinkage;
    readonly confirmations: readonly RouteTraceConfirmation[];
  }[];
  readonly trace_basis: 'LOT' | 'ORDER_FALLBACK';
  readonly complete: boolean;
  readonly missing_operations: readonly number[];
}

export type RouteTraceQuery = { readonly tag_code: string } | { readonly lot_number: string };

export type RouteTraceResult =
  | { readonly status: 'ready'; readonly trace: RouteTrace }
  | { readonly status: 'not-found' }
  | StockFailure;

type Json = Readonly<Record<string, unknown>>;

function obj(value: unknown): Json | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Json) : null;
}

function str(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function strOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function list(value: unknown): readonly Json[] {
  return Array.isArray(value) ? value.map(obj).filter((v): v is Json => v !== null) : [];
}

export function parseRouteTrace(raw: unknown): RouteTrace | null {
  const body = obj(raw);
  if (
    body === null ||
    typeof body.order_no !== 'string' ||
    !Array.isArray(body.planned_route) ||
    !Array.isArray(body.actual) ||
    typeof body.complete !== 'boolean'
  ) {
    return null;
  }
  return {
    order_no: body.order_no,
    product_code: str(body.product_code),
    product_name: str(body.product_name),
    planned_route: list(body.planned_route).map((step) => ({
      operation_seq: Number(step.operation_seq),
      work_center_code: str(step.work_center_code),
      work_center_name: str(step.work_center_name),
    })),
    actual: list(body.actual).map((step) => ({
      operation_seq: Number(step.operation_seq),
      work_center_code: str(step.work_center_code),
      linkage: step.linkage === 'LOT' ? 'LOT' : 'ORDER',
      confirmations: list(step.confirmations).map((c) => ({
        confirmation_id: str(c.confirmation_id),
        started_at: strOrNull(c.started_at),
        ended_at: strOrNull(c.ended_at),
        started_by_employee_id: strOrNull(c.started_by_employee_id),
        qty_good: str(c.qty_good, '0'),
        qty_scrap: str(c.qty_scrap, '0'),
        qty_rework: str(c.qty_rework, '0'),
        status: str(c.status),
        machines: list(c.machines).map((m) => ({ machine_code: str(m.machine_code), name: str(m.name) })),
      })),
    })),
    trace_basis: body.trace_basis === 'LOT' ? 'LOT' : 'ORDER_FALLBACK',
    complete: body.complete,
    missing_operations: Array.isArray(body.missing_operations)
      ? body.missing_operations.filter((n): n is number => typeof n === 'number')
      : [],
  };
}

export async function fetchRouteTrace(
  query: RouteTraceQuery,
  options: StockRequestOptions = {},
): Promise<RouteTraceResult> {
  const search = new URLSearchParams(query as Record<string, string>).toString();
  const result = await callJson('GET', `/v1/route-traces?${search}`, undefined, {}, options);
  if (result.status === 'rejected' && result.httpStatus === 404) {
    return { status: 'not-found' };
  }
  if (result.status !== 'ok') {
    return result;
  }
  const trace = parseRouteTrace(result.payload);
  return trace === null ? { status: 'invalid-response' } : { status: 'ready', trace };
}
